import React, {useEffect} from 'react';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {v4 as uuidv4} from 'uuid';
import {ProgressivePlayer} from '@accurate-player/accurate-player-progressive';
import {
    AudioChannelTrackAdded,
    AudioChannelTrackRemoved,
    ChannelControlEventType,
    ChannelControlPlugin,
    DiscreteAudioPlugin,
    DiscreteAudioTrack,
    DiscreteAudioTrackParameter,
    HotkeyPlugin,
    MutedState,
    Point,
    PointPlugin,
} from '@accurate-player/accurate-player-plugins';
import {ApTimelineBasic} from '@accurate-player/accurate-player-components-external';
import {Player, SeekMode, TimeFormat} from '@accurate-player/accurate-player-core';
import {
    ifChannelControlPlugin,
    ifNotChannelControlPlugin,
    ifNotDiscreteAudioPlugin,
} from '@pluto/player-check/player-check';
import {
    onFullscreen,
    onTimeStep,
    onToggleMute,
    onGetSnapshot,
} from '@pluto/player-controls/player-controls';
import {ResetCategoryAction, SetCategoryAction} from '@pluto/timespan-categories';
import {TimespanCategory} from '@pluto/rest';
import {SetNotificationAction} from '@pluto/notification';
import {batch} from 'react-redux';
import {
    PlayerOrNull,
    PlayerRef,
    ProgressivePlayerOrNull,
} from './AccuratePlayer/AccuratePlayerTypes';
import {
    TimelineMarker,
    QCReportMarker,
    TimeLineRef,
    TimelineRowType,
    MarkupMarker,
    PartialMarkupMarker,
} from './Timeline/TimelineTypes';
import {AudioChannelData, AudioTrackData} from './Timeline/AudioTracks/AudioTracksTypes';
import {
    DisableAudioChannel,
    EnableAudioChannel,
    EnableAudioTrack,
    DisableAudioTrack,
    ResetAudioTrack,
    ResetAudioTracks,
    SetAudioTrack,
    SetAudioAvailableChannelOutput,
} from './Timeline/AudioTracks/Redux/AudioTracksActions';
import VideoAudioQC from './VideoAudioQC';
import {
    CreateQCReportMarkerAction,
    UpdateQCReportMarkerAction,
} from './Timeline/QCReport/Redux/QCReportActions';
import {
    CreateAdBreakMarkerAction,
    UpdateAdBreaksMarkerAction,
} from './Timeline/AdBreaks/Redux/AdBreaksActions';
import {
    CreateMarkupMarkerAction,
    UpdateMarkupMarkerAction,
} from './Timeline/Markup/Redux/MarkupActions';
import {Item, Audio, Channel} from './QCMedia/Redux/QCMediaActions';
import {CreateSnapshotAction} from './QCTabs/Snapshots/Redux/SnapshotActions';

export interface VideoAudioQCContainerProps {
    routeId: string;
    jobDeliveryDate: string;
    item: Item | null;
    markupIssueTypes: TimespanCategory[];
    setCategory: SetCategoryAction;
    resetCategory: ResetCategoryAction;
    setAudioTrack: SetAudioTrack;
    enableAudioTrack: EnableAudioTrack;
    disableAudioTrack: DisableAudioTrack;
    resetAudioTrack: ResetAudioTrack;
    resetAudioTracks: ResetAudioTracks;
    enableAudioChannel: EnableAudioChannel;
    disableAudioChannel: DisableAudioChannel;
    createAdBreakMarker: CreateAdBreakMarkerAction;
    createQCReportMarker: CreateQCReportMarkerAction;
    createMarkupMarker: CreateMarkupMarkerAction;
    updateQCReportMarker: UpdateQCReportMarkerAction;
    updateAdBreaksMarker: UpdateAdBreaksMarkerAction;
    updateMarkupMarker: UpdateMarkupMarkerAction;
    setAudioAvailableChannelOutput: SetAudioAvailableChannelOutput;
    setNotification: SetNotificationAction;
    createSnapshot: CreateSnapshotAction;
    playerRef: PlayerRef;
    timelineRef: TimeLineRef;
}

const equalSrc = curry((src: string, audioTrack: Audio) => {
    return audioTrack.src === src;
});

export const getAudioTrackLabel = (src: string, item: Item): string => {
    const {audioTracks} = item;

    const compareTrack = equalSrc(src);
    const audioTrack = audioTracks.find(compareTrack);

    return audioTrack?.label ?? 'Unknown track';
};

export const getDiscreteAudioPlugin = (player: PlayerOrNull): DiscreteAudioPlugin => {
    return player?.api?.plugin[DiscreteAudioPlugin.Name] as DiscreteAudioPlugin;
};

export const getChannelControlPlugin = (player: PlayerOrNull): ChannelControlPlugin => {
    return player?.api?.plugin[ChannelControlPlugin.Name] as ChannelControlPlugin;
};

export const getPointPlugin = (player: PlayerOrNull): PointPlugin => {
    return player?.api?.plugin[PointPlugin.Name] as PointPlugin;
};

export const getHotkeyPlugin = (player: PlayerOrNull): HotkeyPlugin => {
    return player?.api?.plugin[HotkeyPlugin.Name] as HotkeyPlugin;
};

export const handleEnableTrack = wrap(
    (plugin: DiscreteAudioPlugin, audioTrack: DiscreteAudioTrackParameter) => {
        const {track} = audioTrack;

        const {src} = track;

        plugin.enableTrack(src);
    }
);

export const initDiscreteAudioPlugin = ifNotDiscreteAudioPlugin(
    (player: Player, audioTracks: DiscreteAudioTrackParameter[]) => {
        const plugin = new DiscreteAudioPlugin(player);

        plugin.initDiscreteAudioTracks(audioTracks);

        audioTracks.forEach(handleEnableTrack(plugin));
    }
);

export type AudioPluginProps = {
    item: Item;
    enableAudioChannel: EnableAudioChannel;
    disableAudioChannel: DisableAudioChannel;
    setAudioTrack: SetAudioTrack;
    enableAudioTrack: EnableAudioTrack;
    disableAudioTrack: DisableAudioTrack;
    resetAudioTrack: ResetAudioTrack;
    resetAudioTracks: ResetAudioTracks;
    setAudioAvailableChannelOutput: SetAudioAvailableChannelOutput;
    setNotification: SetNotificationAction;
};

export const mapAudioTrack = (audio: Audio): DiscreteAudioTrackParameter => {
    const {src, label, channels} = audio;

    const channelCount = channels.length;
    const enabled = true;
    const track: DiscreteAudioTrack = {
        src,
        channelCount,
        label,
    };

    return {
        enabled,
        track,
    };
};

export const getPluginAudioTracks = (item: Item): DiscreteAudioTrackParameter[] => {
    return item.audioTracks?.map(mapAudioTrack);
};

export const loadAudioPlugins = (
    player: ProgressivePlayer,
    props: VideoAudioQCContainerProps
) => {
    const {
        item,
        enableAudioChannel,
        disableAudioChannel,
        enableAudioTrack,
        disableAudioTrack,
        resetAudioTrack,
        resetAudioTracks,
        setAudioTrack,
        setAudioAvailableChannelOutput,
        setNotification,
    } = props;

    if (!item) return;

    const audioProps: AudioPluginProps = {
        item,
        enableAudioChannel,
        disableAudioChannel,
        enableAudioTrack,
        disableAudioTrack,
        resetAudioTrack,
        resetAudioTracks,
        setAudioTrack,
        setAudioAvailableChannelOutput,
        setNotification,
    };
    const channelPlugin = getChannelControlPlugin(player);
    initChannelControlPlugin(channelPlugin, player, audioProps);

    const audioPlugin = getDiscreteAudioPlugin(player);
    const audioTracks: DiscreteAudioTrackParameter[] = getPluginAudioTracks(item);
    initDiscreteAudioPlugin(audioPlugin, player, audioTracks);
};

export const mapAudioChannelData = curry((enable: boolean, channel: Channel) => {
    const {label, waveformUrl} = channel;
    const mutedState = enable ? MutedState.Playing : MutedState.ExplicitMuted;

    return {
        label,
        mutedState,
        waveformUrl,
    };
});

export const createChannelData = (
    enable: boolean,
    audioTrack: Audio,
    channelCount: number
): AudioChannelData[] => {
    if (channelCount !== audioTrack.channels.length) {
        console.warn(
            'Number of audio channels in video player doesnt match the number of channels reported by the API.'
        );
        return [];
    }

    const handleChannelMap = mapAudioChannelData(enable);

    return audioTrack.channels.map(handleChannelMap);
};

export const sameSrc = curry((src: string, track: Audio) => {
    return track.src === src;
});

export type PlayerPluginProps = {
    player: Player;
    plugin: ChannelControlPlugin;
    props: AudioPluginProps;
    outputMono: boolean | undefined;
    disableAudioChannel: DisableAudioChannel;
};

export interface DispatchAudioChannelCreatedProps {
    trackData: AudioTrackData;
    plugin: ChannelControlPlugin;
    disableAudioChannel: DisableAudioChannel;
    setAudioTrack: SetAudioTrack;
    enabled: boolean;
    muteAudioTrack: (
        plugin: ChannelControlPlugin,
        disableAudioChannel: DisableAudioChannel,
        trackData: AudioTrackData
    ) => void;
}

export const dispatchAudioChannelCreated = wrap(
    (props: DispatchAudioChannelCreatedProps) => {
        const {
            trackData,
            plugin,
            disableAudioChannel,
            setAudioTrack,
            enabled,
            muteAudioTrack,
        } = props;

        setAudioTrack(trackData);

        if (!enabled) muteAudioTrack(plugin, disableAudioChannel, trackData);
    }
);

export const onAudioChannelCreated = curry(
    (playerData: PlayerPluginProps, event: AudioChannelTrackAdded) => {
        const {player, props, plugin, outputMono, disableAudioChannel} = playerData;
        const {item, setAudioTrack} = props;
        const {audioTracks} = item;
        const {data, src} = event;

        const muxed = src === player.master.src;
        const channelCount = data.gains.length;

        // Reroute each tracks to mono if only have mono output available
        if (outputMono) {
            const monoRouteMap = [[1]];

            plugin.rerouteChannels(src, monoRouteMap);
        }

        if (muxed) {
            // mute muxed audio and don't add to timeline.
            muteMuxedAudioTrack(plugin, src, channelCount);
            return;
        }

        const label = getAudioTrackLabel(src, item);
        const sameTrack = sameSrc(src);
        const trackIndex = audioTracks.findIndex(sameTrack);
        const audioTrack = audioTracks[trackIndex];
        const enabled = trackIndex === 0;
        const {waveformUrl} = audioTrack;
        const enableAudioChannels = enabled && !outputMono;
        const channelData = createChannelData(
            enableAudioChannels,
            audioTrack,
            channelCount
        );

        const trackData: AudioTrackData = {
            src,
            label,
            channelData,
            enabled,
            muxed,
            waveformUrl,
        };

        batch(
            dispatchAudioChannelCreated({
                trackData,
                plugin,
                disableAudioChannel,
                setAudioTrack,
                enabled,
                muteAudioTrack,
            })
        );
    }
);

export const onAudioChannelRemoved = curry(
    (props: AudioPluginProps, event: AudioChannelTrackRemoved) => {
        const {resetAudioTrack} = props;
        const {src} = event;

        resetAudioTrack({
            src,
        });
    }
);

export interface DispatchAudioEventsProps {
    setAudioAvailableChannelOutput: SetAudioAvailableChannelOutput;
    setNotification: SetNotificationAction;
    outputMono: boolean;
    outputChannelCount: number;
}

export const dispatchAudioEvents = wrap((props: DispatchAudioEventsProps) => {
    const {
        setAudioAvailableChannelOutput,
        setNotification,
        outputMono,
        outputChannelCount,
    } = props;

    // Detect if we only have mono output available
    if (outputMono) {
        setAudioAvailableChannelOutput(outputChannelCount);

        const errorMessage =
            'Bluetooth headset detected, all audio will be played as mono';

        console.error(errorMessage);

        setNotification({
            type: 'warning',
            text: errorMessage,
            manualHide: true,
        });
    }
});

/* istanbul ignore file */
export const initChannelControlPlugin = ifNotChannelControlPlugin(
    (player: Player, props: AudioPluginProps) => {
        const plugin = new ChannelControlPlugin(player);
        const {
            setAudioAvailableChannelOutput,
            setNotification,
            disableAudioChannel,
        } = props;
        const outputChannelCount = plugin.getOutputChannelCount();
        const outputMono = isOutputChannelMono(plugin);

        batch(
            dispatchAudioEvents({
                setAudioAvailableChannelOutput,
                setNotification,
                outputMono,
                outputChannelCount,
            })
        );

        const handleAudioChannelCreated = onAudioChannelCreated({
            outputMono,
            player,
            plugin,
            props,
            disableAudioChannel,
        });
        const handleAudioChannelRemoved = onAudioChannelRemoved(props);

        plugin.on(ChannelControlEventType.Added, handleAudioChannelCreated);
        plugin.on(ChannelControlEventType.Removed, handleAudioChannelRemoved);
    }
);

export type RefProps = {
    playerRef: PlayerRef;
    props: VideoAudioQCContainerProps;
};

type TrackClick = {
    plugin: ChannelControlPlugin;
    trackData: AudioTrackData;
    allTracks: AudioTrackData[];
    props: VideoAudioQCContainerProps;
};

export const mapMutedChannel = (channelData: AudioChannelData) => {
    return channelData.mutedState === MutedState.ExplicitMuted;
};

export interface DispatchDisableAudioChannelProps {
    trackData: AudioTrackData;
    channel: number;
    disableAudioChannel: DisableAudioChannel;
    disableAudioTrack: DisableAudioTrack;
}

export const dispatchDisableAudioChannel = wrap(
    (props: DispatchDisableAudioChannelProps) => {
        const {trackData, channel, disableAudioChannel, disableAudioTrack} = props;

        disableAudioChannel({
            channel,
            trackData,
        });

        const allChannelsMuted = trackData.channelData.every(mapMutedChannel);

        if (allChannelsMuted) disableAudioTrack(trackData);
    }
);

type ChannelClick = {
    plugin: ChannelControlPlugin;
    trackData: AudioTrackData;
    allTracks: AudioTrackData[];
    props: VideoAudioQCContainerProps;
    channel: number;
};

export const handleChannelToggleMute = ifChannelControlPlugin(
    (channelClick: ChannelClick) => {
        const {plugin, trackData, allTracks, props, channel} = channelClick;

        const {
            disableAudioChannel,
            enableAudioChannel,
            enableAudioTrack,
            disableAudioTrack,
        } = props;

        if (trackData.channelData[channel].mutedState === MutedState.Playing) {
            plugin.mute(trackData.src, channel);

            batch(
                dispatchDisableAudioChannel({
                    trackData,
                    channel,
                    disableAudioChannel,
                    disableAudioTrack,
                })
            );

            return;
        }

        if (!trackData.enabled) {
            const handleMuteTrack = muteAudioTrack(plugin, disableAudioChannel);
            const handleFilterTrack = filterTrack(trackData);

            allTracks.filter(handleFilterTrack).map(handleMuteTrack);
            enableAudioTrack(trackData);
        }

        plugin.unmute(trackData.src, channel);
        enableAudioChannel({
            channel,
            trackData,
        });
    }
);

export const onChannelToggleMute = curry(
    (
        refProps: RefProps,
        channel: number,
        allTracks: AudioTrackData[],
        trackData: AudioTrackData
    ) => {
        const {playerRef, props} = refProps;
        const player = playerRef.current;

        const plugin = getChannelControlPlugin(player);
        const channelClick = {
            plugin,
            allTracks,
            trackData,
            props,
            channel,
        };

        handleChannelToggleMute(plugin, channelClick);
    }
);

export const handleTrackClick = ifChannelControlPlugin((trackClick: TrackClick) => {
    const {plugin, trackData, allTracks, props} = trackClick;
    const {
        disableAudioChannel,
        enableAudioChannel,
        enableAudioTrack,
        disableAudioTrack,
    } = props;

    const handleMuteTrack = muteAudioTrack(plugin, disableAudioChannel);
    const handleUnmuteTrack = unmuteAudioTrack(plugin, enableAudioChannel);
    const handleFilterTrack = filterTrack(trackData);

    allTracks.filter(handleFilterTrack).map(handleMuteTrack);

    const {enabled} = trackData;

    if (enabled) {
        handleMuteTrack(trackData);
        disableAudioTrack(trackData);
    } else {
        handleUnmuteTrack(trackData);
        enableAudioTrack(trackData);
    }
});

export const onAudioTrackClick = curry(
    (refProps: RefProps, allTracks: AudioTrackData[], trackData: AudioTrackData) => {
        const {playerRef, props} = refProps;
        const player = playerRef.current;

        const plugin = getChannelControlPlugin(player);
        const trackClick = {
            plugin,
            allTracks,
            trackData,
            props,
        };

        handleTrackClick(plugin, trackClick);
    }
);

export const filterTrack = curry((trackA: AudioTrackData, trackB: AudioTrackData) => {
    return trackA.src !== trackB.src;
});

export const muteMuxedAudioTrack = (
    plugin: ChannelControlPlugin,
    src: string,
    channelCount: number
) => {
    for (let i = 0; i < channelCount; i++) {
        plugin.mute(src, i);
    }
};

export type OnMuteChannelProps = {
    plugin: ChannelControlPlugin;
    disableAudioChannel: DisableAudioChannel;
    trackData: AudioTrackData;
};

export const onMuteChannel = curry(
    (props: OnMuteChannelProps, _data: AudioChannelData, index: number) => {
        const {plugin, disableAudioChannel, trackData} = props;
        const {src} = trackData;

        plugin.mute(src, index);
        disableAudioChannel({
            trackData,
            channel: index,
        });
    }
);

export const isOutputChannelMono = (plugin: ChannelControlPlugin) => {
    return plugin.getOutputChannelCount() < 2;
};

export const muteAudioTrack = curry(
    (
        plugin: ChannelControlPlugin,
        disableAudioChannel: DisableAudioChannel,
        trackData: AudioTrackData
    ) => {
        const {channelData} = trackData;

        if (isOutputChannelMono(plugin)) {
            const {src} = trackData;

            plugin.mute(src, 0);

            return;
        }

        const handleMuteChannel = onMuteChannel({
            plugin,
            disableAudioChannel,
            trackData,
        });

        channelData.forEach(handleMuteChannel);
    }
);

export type OnUnmuteChannelProps = {
    plugin: ChannelControlPlugin;
    enableAudioChannel: EnableAudioChannel;
    trackData: AudioTrackData;
};

export const onUnmuteChannel = curry(
    (props: OnUnmuteChannelProps, _data: AudioChannelData, index: number) => {
        const {plugin, enableAudioChannel, trackData} = props;
        const {src} = trackData;

        plugin.unmute(src, index);
        enableAudioChannel({
            trackData,
            channel: index,
        });
    }
);

export const unmuteAudioTrack = curry(
    (
        plugin: ChannelControlPlugin,
        enableAudioChannel: EnableAudioChannel,
        trackData: AudioTrackData
    ) => {
        const {channelData} = trackData;

        if (isOutputChannelMono(plugin)) {
            const {src} = trackData;

            plugin.unmute(src, 0);

            return;
        }

        const handleUnmuteChannel = onUnmuteChannel({
            plugin,
            enableAudioChannel,
            trackData,
        });
        channelData.forEach(handleUnmuteChannel);
    }
);

export const updateTimelineRef = (
    timelineRef: TimeLineRef,
    player: ProgressivePlayerOrNull
) => {
    if (timelineRef.current && player) {
        timelineRef.current.player = player;
    }
};

const onTimelineCreated = curry(
    (timelineRef: TimeLineRef, playerRef: PlayerRef, timeline: ApTimelineBasic) => {
        timelineRef.current = timeline;
        updateTimelineRef(timelineRef, playerRef.current);
    }
);

const onTimelineDestroyed = wrap((timelineRef: TimeLineRef) => {
    timelineRef.current = null;
});

export interface OnPlayerCreatedProps {
    videoAudioQCPops: VideoAudioQCContainerProps;
    playerRef: PlayerRef;
    timelineRef: TimeLineRef;
    setAudioAvailableChannelOutput: SetAudioAvailableChannelOutput;
    setNotification: SetNotificationAction;
}

export const onPlayerCreated = curry(
    (props: OnPlayerCreatedProps, player: ProgressivePlayer) => {
        const {videoAudioQCPops, playerRef, timelineRef} = props;

        playerRef.current = player;

        updateTimelineRef(timelineRef, player);
        loadAudioPlugins(player, videoAudioQCPops);
    }
);

const onPlayerDestroyed = wrap((playerRef: PlayerRef) => {
    playerRef.current = null;
});

export const onPlay = wrap((playerRef: PlayerRef) => {
    const player = playerRef.current?.api;

    if (player && player.getStatus().paused) {
        player.play();
    }
});

export const onPause = wrap((playerRef: PlayerRef) => {
    const player = playerRef.current?.api;

    if (player && !player.getStatus().paused) {
        player.pause();
    }
});

export const onThumbnailClick = curry((playerRef: PlayerRef, marker: TimelineMarker) => {
    const {start} = marker;
    const player = playerRef.current;

    if (!player) return;

    player.api.seek({
        time: Math.round(start),
        format: TimeFormat.Frame,
        mode: SeekMode.Absolute,
    });
});

export const onVolumeChange = curry(
    (playerRef: PlayerRef, _event: any, volume: number) => {
        const player = playerRef.current;

        if (!player) return;

        player.api.setVolume(volume);

        if (player.api.getStatus().muted) {
            player.api.toggleMute();
        }
    }
);

export type OnSetProps = {
    item: Item | null;
    inpoint: boolean;
    playerRef: PlayerRef;
    updateQCReportMarker: UpdateQCReportMarkerAction;
    updateAdBreaksMarker: UpdateAdBreaksMarkerAction;
    updateMarkupMarker: UpdateMarkupMarkerAction;
};

export type OnEditMarkerProps = {
    selectedMarker: TimelineMarker;
    selectedType: TimelineRowType;
};

export const updateMarkerInOut = curry(
    (props: OnSetProps, markerProps: OnEditMarkerProps) => {
        const {
            item,
            inpoint,
            playerRef,
            updateQCReportMarker,
            updateAdBreaksMarker,
            updateMarkupMarker,
        } = props;
        const {selectedMarker, selectedType} = markerProps;

        if (!item) return;

        const player = playerRef.current;
        const {duration} = item.video;

        const currentTime = player?.api.getCurrentTime(TimeFormat.Frame) as number;
        switch (selectedType) {
            case TimelineRowType.QCReport:
                const noteMarker: QCReportMarker = {
                    ...selectedMarker,
                    start: inpoint ? currentTime : selectedMarker.start,
                    end: !inpoint ? currentTime : selectedMarker.end,
                    details: '',
                };

                updateQCReportMarker({
                    url: item.notes,
                    duration,
                    marker: noteMarker,
                    inpoint,
                });
                break;
            case TimelineRowType.AdBreak:
                const start = currentTime;
                const adBreaksMarker: TimelineMarker = {
                    ...selectedMarker,
                    start,
                };

                updateAdBreaksMarker(item.adBreaks, duration, adBreaksMarker);
                break;
            case TimelineRowType.Markup:
                const markupMarker: PartialMarkupMarker = {
                    ...selectedMarker,
                    start: inpoint ? currentTime : selectedMarker.start,
                    end: !inpoint ? currentTime : selectedMarker.end,
                };

                updateMarkupMarker({
                    url: item.markups,
                    duration,
                    marker: markupMarker,
                    inpoint,
                });
                break;
        }
    }
);

export const setPluginInOut = (playerRef: PlayerRef, inpoint: boolean) => {
    const player = playerRef.current;
    const plugin = getPointPlugin(player);

    if (!plugin) return;

    if (inpoint) {
        plugin.setInPoint();
        return;
    }

    plugin.setOutPoint();
};

export const onSetInOut = wrap(
    (
        props: OnSetProps,
        selectedType: TimelineRowType,
        selectedMarker: TimelineMarker | undefined
    ) => {
        const {inpoint, playerRef} = props;

        if (!selectedMarker) {
            setPluginInOut(playerRef, inpoint);
            return;
        }

        updateMarkerInOut(props, {
            selectedMarker,
            selectedType,
        });
    }
);

export const clearPluginPoints = (playerRef: PlayerRef) => {
    const player = playerRef.current;
    const plugin = getPointPlugin(player);

    if (!plugin) return;

    plugin.clearPoints();
};

export type GoToInOutPointProps = {
    playerRef: PlayerRef;
    inPoint: boolean;
    onGoToFrame: (playerRef: PlayerRef, frame: number) => void;
};

export const onGoToInOutPoint = wrap((props: GoToInOutPointProps) => {
    const {playerRef, inPoint, onGoToFrame} = props;
    const plugin = getPointPlugin(playerRef.current);

    if (!plugin) return;

    const {points} = plugin;
    const point = inPoint ? points.in : points.out;

    if (point) {
        const {frame} = point;

        onGoToFrame(playerRef, frame);
    }
});

export const onClearPoints = wrap((playerRef: PlayerRef) => {
    clearPluginPoints(playerRef);
});

export const onGoToFrame = curry((playerRef: PlayerRef, time: number | string) => {
    const player = playerRef.current;

    if (!player) return;

    player.api.seek({
        time,
        format: TimeFormat.Frame,
        mode: SeekMode.Absolute,
    });
});

type PointOrUndefined = Point | undefined;

export type CreateQCReportMarkerProps = {
    currentTime: number;
    inPoint: PointOrUndefined;
    outPoint: PointOrUndefined;
    identifier: string;
};

export const createQCReportMarkerInstance = (
    props: CreateQCReportMarkerProps
): QCReportMarker => {
    const {currentTime, inPoint, outPoint, identifier} = props;
    let start;
    let end;

    if (inPoint && outPoint) {
        const {frame: inFrame} = inPoint;
        const {frame: outFrame} = outPoint;
        start = Math.min(inFrame, outFrame);
        end = Math.max(inFrame, outFrame);
    } else {
        start = currentTime;
        end = start + 1;
    }

    const marker: QCReportMarker = {
        identifier,
        details: '',
        start,
        end,
    };

    return marker;
};

export type CreateQCNoteProps = {
    item: Item | null;
    playerRef: PlayerRef;
    createQCReportMarker: CreateQCReportMarkerAction;
};

export const onCreateQCNote = curry((props: CreateQCNoteProps, rowId: string) => {
    const {item, playerRef, createQCReportMarker} = props;
    const player = playerRef.current;

    if (!player || !item) return;

    const plugin = getPointPlugin(player);
    const {points} = plugin;
    const inPoint = points?.in;
    const outPoint = points?.out;

    if (inPoint || outPoint) plugin.clearPoints();

    const identifier = uuidv4();
    const currentTime = player.api.getCurrentTime(TimeFormat.Frame) as number;
    const newMarker = createQCReportMarkerInstance({
        currentTime,
        inPoint,
        outPoint,
        identifier,
    });
    const {duration} = item.video;

    createQCReportMarker(item.notes, duration, rowId, newMarker);
});

export type CreateAdBreakProps = {
    item: Item | null;
    playerRef: PlayerRef;
    createAdBreakMarker: CreateAdBreakMarkerAction;
};

export const onCreateAdBreak = curry((props: CreateAdBreakProps, rowId: string) => {
    const {playerRef, createAdBreakMarker, item} = props;
    const player = playerRef.current;

    if (!player || !item) return;

    const {adBreaks} = item;
    const {duration} = item.video;

    const start = player.api.getCurrentTime(TimeFormat.Frame) as number;

    createAdBreakMarker(adBreaks, duration, rowId, start);
});

export const createMarkupMarkerInstance = (
    currentTime: number,
    inPoint: PointOrUndefined,
    outPoint: PointOrUndefined,
    identifier: string,
    issue: number
): MarkupMarker => {
    let start;
    let end;

    if (inPoint && outPoint) {
        const {frame: inFrame} = inPoint;
        const {frame: outFrame} = outPoint;
        start = Math.min(inFrame, outFrame);
        end = Math.max(inFrame, outFrame);
    } else {
        start = currentTime;
        end = start + 1;
    }

    const marker: MarkupMarker = {
        identifier,
        start,
        end,
        issue,
    };

    return marker;
};

export type CreateMarkupProps = {
    item: Item | null;
    playerRef: PlayerRef;
    markupIssueTypes: TimespanCategory[];
    createMarkupMarker: CreateMarkupMarkerAction;
};

export const onCreateMarkup = curry((props: CreateMarkupProps, rowId: string) => {
    const {item, playerRef, markupIssueTypes, createMarkupMarker} = props;
    const [category] = markupIssueTypes;
    const player = playerRef.current;

    if (!player || !item) return;

    const plugin = getPointPlugin(player);
    const {points} = plugin;
    const inPoint = points?.in;
    const outPoint = points?.out;

    if (inPoint || outPoint) plugin.clearPoints();

    const identifier = uuidv4();
    const issue = category.id;
    const currentTime = player.api.getCurrentTime(TimeFormat.Frame) as number;
    const newMarker = createMarkupMarkerInstance(
        currentTime,
        inPoint,
        outPoint,
        identifier,
        issue
    );
    const {markups} = item;
    const {duration} = item.video;

    createMarkupMarker(markups, duration, rowId, newMarker);
});

export const windowDispatchEvent = wrap((playerRef: PlayerRef) => {
    window.dispatchEvent(new Event('focus'));

    const player = playerRef.current;

    if (player && player.api.master && player.api.getStatus()) {
        if (!player.api.master.paused && player.api.getStatus().paused) {
            player.api.play();
        }

        if (player.api.master.paused && !player?.api.getStatus().paused) {
            player.api.pause();
        }
    }
});

export const removeDocumentEventListener = () => {
    document.removeEventListener('fullscreenchange', windowDispatchEvent);
};

export const onFullscreenFocusEffect = wrap((playerRef: PlayerRef) => {
    document.addEventListener('fullscreenchange', windowDispatchEvent(playerRef));

    return removeDocumentEventListener;
});

export const onUnmount = wrap((props: VideoAudioQCContainerProps) => {
    const {resetCategory, resetAudioTracks} = props;

    resetAudioTracks();
    resetCategory('media');
    resetCategory('markup');
});

export const handleMount = wrap((props: VideoAudioQCContainerProps) => {
    const {setCategory} = props;

    setCategory('media');
    setCategory('markup');

    return onUnmount(props);
});

function VideoAudioQCContainer(props: VideoAudioQCContainerProps) {
    const {
        routeId,
        item,
        markupIssueTypes,
        createAdBreakMarker,
        createQCReportMarker,
        createMarkupMarker,
        updateQCReportMarker,
        updateAdBreaksMarker,
        updateMarkupMarker,
        setAudioAvailableChannelOutput,
        setNotification,
        jobDeliveryDate,
        playerRef,
        createSnapshot,
        timelineRef,
    } = props;
    const onPlayerCreatedProps: OnPlayerCreatedProps = {
        videoAudioQCPops: props,
        playerRef,
        timelineRef,
        setAudioAvailableChannelOutput,
        setNotification,
    };

    const handleGetSnapshot = onGetSnapshot(
        playerRef,
        createSnapshot,
        item?.video.id,
        routeId
    );
    const handlePlayerCreated = onPlayerCreated(onPlayerCreatedProps);
    const handlePlayerDestroyed = onPlayerDestroyed(playerRef);
    const handlePlay = onPlay(playerRef);
    const handlePause = onPause(playerRef);
    const handleOneFrameForward = onTimeStep(
        playerRef,
        1,
        SeekMode.RelativeForward,
        TimeFormat.Frame
    );
    const handleOneFrameBack = onTimeStep(
        playerRef,
        1,
        SeekMode.RelativeBackward,
        TimeFormat.Frame
    );
    const handleFiveSecondsForward = onTimeStep(
        playerRef,
        5,
        SeekMode.RelativeForward,
        TimeFormat.Seconds
    );
    const handleFiveSecondsBack = onTimeStep(
        playerRef,
        5,
        SeekMode.RelativeBackward,
        TimeFormat.Seconds
    );
    const handleFullscreen = onFullscreen(playerRef);
    const handleTimelineCreated = onTimelineCreated(timelineRef, playerRef);
    const handleTimelineDestroyed = onTimelineDestroyed(playerRef);
    const handleThumbnailClick = onThumbnailClick(playerRef);
    const handleAudioTrackClick = onAudioTrackClick({
        playerRef,
        props,
    });
    const handleChannelToggleMute = onChannelToggleMute({
        playerRef,
        props,
    });

    const handleVolumeChange = onVolumeChange(playerRef);
    const handleToggleMute = onToggleMute(playerRef);
    const handleSetIn = onSetInOut({
        item,
        inpoint: true,
        playerRef,
        updateQCReportMarker,
        updateAdBreaksMarker,
        updateMarkupMarker,
    });
    const handleSetOut = onSetInOut({
        item,
        inpoint: false,
        playerRef,
        updateQCReportMarker,
        updateAdBreaksMarker,
        updateMarkupMarker,
    });
    const handleClearPoints = onClearPoints(playerRef);

    const createQCNoteProps: CreateQCNoteProps = {
        item,
        playerRef,
        createQCReportMarker,
    };
    const handleCreateQCNote = onCreateQCNote(createQCNoteProps);

    const createAdBreakProps: CreateAdBreakProps = {
        item,
        playerRef,
        createAdBreakMarker,
    };
    const handleCreateAdBreak = onCreateAdBreak(createAdBreakProps);

    const createMarkupProps: CreateMarkupProps = {
        item,
        playerRef,
        markupIssueTypes,
        createMarkupMarker,
    };
    const handleCreateMarkup = onCreateMarkup(createMarkupProps);

    const handleEditTabMarkerIn = updateMarkerInOut({
        item,
        inpoint: true,
        playerRef,
        updateQCReportMarker,
        updateAdBreaksMarker,
        updateMarkupMarker,
    });
    const handleEditTabMarkerOut = updateMarkerInOut({
        item,
        inpoint: false,
        playerRef,
        updateQCReportMarker,
        updateAdBreaksMarker,
        updateMarkupMarker,
    });
    const handleGoToFrame = onGoToFrame(playerRef);
    const handleGoToInPoint = onGoToInOutPoint({
        playerRef,
        inPoint: true,
        onGoToFrame,
    });
    const handleGoToOutPoint = onGoToInOutPoint({
        playerRef,
        inPoint: false,
        onGoToFrame,
    });

    // Needed for hotkeys to work -  to solve bug where Chrome on OS X does not
    // send a "keyup" event when entering fullscreen
    useEffect(onFullscreenFocusEffect(playerRef), []);

    const onMount = handleMount(props, routeId);
    useEffect(onMount, []);

    return (
        <VideoAudioQC
            onGetSnapshot={handleGetSnapshot}
            item={item}
            jobDeliveryDate={jobDeliveryDate}
            markupIssueTypes={markupIssueTypes}
            onPlayerCreated={handlePlayerCreated}
            onStepOneFrameBack={handleOneFrameBack}
            onStepOneFrameForward={handleOneFrameForward}
            onPlay={handlePlay}
            onPause={handlePause}
            onToggleMute={handleToggleMute}
            onFiveSecondsForward={handleFiveSecondsForward}
            onFiveSecondsBack={handleFiveSecondsBack}
            onVolumeChange={handleVolumeChange}
            onFullscreen={handleFullscreen}
            onSetIn={handleSetIn}
            onSetOut={handleSetOut}
            onClearPoints={handleClearPoints}
            onCreateQCNote={handleCreateQCNote}
            onCreateAdBreak={handleCreateAdBreak}
            onCreateMarkup={handleCreateMarkup}
            onEditTabMarkerIn={handleEditTabMarkerIn}
            onEditTabMarkerOut={handleEditTabMarkerOut}
            onGoToFrame={handleGoToFrame}
            onGoToIn={handleGoToInPoint}
            onGoToOut={handleGoToOutPoint}
            onPlayerDestroyed={handlePlayerDestroyed}
            onTimelineCreated={handleTimelineCreated}
            onTimelineDestroyed={handleTimelineDestroyed}
            onThumbnailClick={handleThumbnailClick}
            onAudioTrackClick={handleAudioTrackClick}
            onAudioChannelClick={handleChannelToggleMute}
            playerRef={playerRef}
        />
    );
}

export default React.memo(VideoAudioQCContainer);
